import { useEffect, useState } from 'react';
import { CheckCircle2, CloudOff, RefreshCw, Send, XCircle } from 'lucide-react';
import client, { apiError } from '../adminApi';

// Shared Amden Reach sync UI for enquiry/booking records. Manual sync only —
// the button click is the review decision. Hidden entirely when the server
// has no Reach credentials configured.
let statusCache = null;

export const useCrmConfigured = () => {
  const [configured, setConfigured] = useState(statusCache);
  useEffect(() => {
    if (statusCache !== null) return undefined;
    let alive = true;
    client.get('/admin/crm/status')
      .then((res) => { statusCache = !!res.data.configured; alive && setConfigured(statusCache); })
      .catch(() => { statusCache = false; alive && setConfigured(false); });
    return () => { alive = false; };
  }, []);
  return configured;
};

// Compact list badge: teal when synced, red on failure, nothing otherwise.
export const CrmBadge = ({ record }) => {
  if (record.crmSyncStatus === 'synced') {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold uppercase text-teal-800" data-testid="crm-badge-synced">
        In Reach
      </span>
    );
  }
  if (record.crmSyncStatus === 'failed') {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700" data-testid="crm-badge-failed">
        Sync failed
      </span>
    );
  }
  return null;
};

// Detail-dialog panel with status, Reach reference codes and the sync action.
export const CrmSyncPanel = ({ kind, record, onUpdate }) => {
  const configured = useCrmConfigured();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!configured || !record) return null;
  if (record.crmSyncStatus === 'not_required') return null; // spam-flagged

  const id = kind === 'booking' ? record.bookingId : record.enquiryId;
  const path = kind === 'booking'
    ? `/admin/booking/bookings/${id}/sync-crm`
    : `/admin/enquiries/${id}/sync-crm`;

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const res = await client.post(path);
      onUpdate(res.data.item);
    } catch (err) {
      setError(apiError(err, 'Reach sync failed.'));
    } finally {
      setSending(false);
    }
  };

  const synced = record.crmSyncStatus === 'synced';
  const failed = record.crmSyncStatus === 'failed';
  const reach = record.crmReach || {};
  const refCode = reach.opportunityCode || reach.customerCode || record.crmRecordId;

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="crm-sync-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
          Amden Reach CRM
          {synced && (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold uppercase text-teal-800" data-testid="crm-sync-status">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Synced
            </span>
          )}
          {failed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700" data-testid="crm-sync-status">
              <XCircle className="h-3 w-3" aria-hidden="true" /> Failed
            </span>
          )}
          {!synced && !failed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600" data-testid="crm-sync-status">
              <CloudOff className="h-3 w-3" aria-hidden="true" /> Not sent
            </span>
          )}
        </p>
        {!synced && (
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="crm-sync-button"
            className="inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-navy-700 disabled:opacity-50"
          >
            {sending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Send className="h-3.5 w-3.5" aria-hidden="true" />}
            {sending ? 'Sending…' : failed ? 'Retry Reach Sync' : 'Send to Reach CRM'}
          </button>
        )}
      </div>
      {synced && refCode && (
        <p className="mt-2 text-xs text-slate-500">
          Reach record <span className="font-mono font-bold text-navy-900">{refCode}</span>
          {reach.customerCode && reach.opportunityCode && ` · Customer ${reach.customerCode}`}
          {record.crmSyncedAt && ` · ${new Date(record.crmSyncedAt).toLocaleString('en-IE')}`}
        </p>
      )}
      {failed && record.crmSyncError && (
        <p className="mt-2 text-xs font-semibold text-red-700" data-testid="crm-sync-error">{record.crmSyncError}</p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs font-semibold text-red-700" data-testid="crm-sync-error">{error}</p>
      )}
    </div>
  );
};
